import { directSupabase } from './supabaseClient';
import type { Database } from '../types/supabase';

export type EmailActivityLog = Database['public']['Tables']['email_activity_log']['Row'];
export type EmailActivityLogInsert = Database['public']['Tables']['email_activity_log']['Insert'];

export type EmailStats = {
  total: number;
  byStatus: {
    sent: number;
    failed: number;
    draft: number;
  };
  byTemplateType: Record<string, number>;
  last24Hours: number;
  last7Days: number;
};

export type ServiceResponse<T> = {
  data: T | null;
  error: string | null;
};

export const emailActivity = {
  /**
   * Log an email activity
   */
  log: async (log: EmailActivityLogInsert) => {
    try {
      const { data, error } = await directSupabase
        .from('email_activity_log')
        .insert([log])
        .select()
        .single();
      
      if (error) {
        return { data: null, error: error.message };
      } else {
        return { data, error: null };
      }
    } catch (error) {
      return { data: null, error: error instanceof Error ? error.message : 'Failed to log email activity' };
    }
  },

  /**
   * Get recent email activity with pagination
   */
  getRecent: async (page: number = 1, pageSize: number = 20, filters?: {
    templateType?: string;
    recipient?: string;
    status?: string;
    customerAction?: string;
    startDate?: Date;
    endDate?: Date;
    projectId?: string;
  }) => {
    try {
      let query = directSupabase
        .from('email_activity_log')
        .select('*', { count: 'exact' });
      
      // Apply filters if provided
      if (filters?.templateType) {
        query = query.eq('template_type', filters.templateType);
      }
      if (filters?.recipient) {
        query = query.ilike('recipient', `%${filters.recipient}%`);
      }
      if (filters?.status) {
        query = query.eq('status', filters.status);
      }
      if (filters?.customerAction) {
        const action = String(filters.customerAction).trim().toLowerCase();
        if (action === 'accept') {
          query = query.or([
            'metadata->customerAction->>action.eq.accept',
            'metadata->customerAction->>action.eq.accepted',
            'metadata->customer_action->>action.eq.accept',
            'metadata->customer_action->>action.eq.accepted',
          ].join(','));
        } else if (action === 'reject') {
          query = query.or([
            'metadata->customerAction->>action.eq.reject',
            'metadata->customerAction->>action.eq.rejected',
            'metadata->customer_action->>action.eq.reject',
            'metadata->customer_action->>action.eq.rejected',
          ].join(','));
        } else if (action === 'query') {
          query = query.or([
            'metadata->customerAction->>action.eq.query',
            'metadata->customer_action->>action.eq.query',
          ].join(','));
        } else if (action === 'opened') {
          query = query.or([
            'metadata->customerAction->>action.eq.opened',
            'metadata->customerAction->>action.eq.open',
            'metadata->customer_action->>action.eq.opened',
            'metadata->customer_action->>action.eq.open',
          ].join(','));
        } else if (action === 'none') {
          query = query.or([
            'metadata->customerAction->>action.is.null',
            'metadata->customer_action->>action.is.null',
          ].join(','));
        }
      }
      if (filters?.projectId) {
        query = query.eq('project_id', filters.projectId);
      }
      if (filters?.startDate) {
        query = query.gte('created_at', filters.startDate.toISOString());
      }
      if (filters?.endDate) {
        query = query.lte('created_at', filters.endDate.toISOString());
      }
      
      // Apply pagination
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      query = query.range(from, to).order('created_at', { ascending: false });
      
      const { data, error, count } = await query;
      
      if (error) {
        return { 
          data: [], 
          error: error.message,
          count: 0
        };
      } else {
        return { 
          data: data as EmailActivityLog[], 
          error: null,
          count: count || 0
        };
      }
    } catch (error) {
      return { 
        data: [], 
        error: error instanceof Error ? error.message : 'Failed to fetch email activity',
        count: 0
      };
    }
  },

  /**
   * Get a specific email activity by ID
   */
  getById: async (id: string) => {
    try {
      const { data, error } = await directSupabase
        .from('email_activity_log')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) {
        return { data: null, error: error.message };
      } else {
        return { data: data as EmailActivityLog, error: null };
      }
    } catch (error) {
      return { data: null, error: error instanceof Error ? error.message : 'Failed to fetch email activity' };
    }
  },

  /**
   * Get email activity statistics
   */
  getStats: async (): Promise<ServiceResponse<EmailStats>> => {
    try {
      // Get total count
      const { count: total } = await directSupabase
        .from('email_activity_log')
        .select('*', { count: 'exact', head: true });
      
      // Get counts by status
      const { count: sent } = await directSupabase
        .from('email_activity_log')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'sent');
      
      const { count: failed } = await directSupabase
        .from('email_activity_log')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'failed');
      
      const { count: draft } = await directSupabase
        .from('email_activity_log')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'draft');
      
      // Get counts by template type
      const { data: templateTypeData } = await directSupabase
        .from('email_activity_log')
        .select('template_type, status');
      
      const byTemplateType: Record<string, number> = {};
      if (templateTypeData) {
        templateTypeData.forEach(log => {
          if (log.template_type) {
            byTemplateType[log.template_type] = (byTemplateType[log.template_type] || 0) + 1;
          }
        });
      }
      
      // Get recent activity counts
      const now = new Date();
      const last24Hours = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      const last7Days = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      
      const { count: last24HoursCount } = await directSupabase
        .from('email_activity_log')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', last24Hours.toISOString());
      
      const { count: last7DaysCount } = await directSupabase
        .from('email_activity_log')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', last7Days.toISOString());
      
      const stats: EmailStats = {
        total: total || 0,
        byStatus: {
          sent: sent || 0,
          failed: failed || 0,
          draft: draft || 0,
        },
        byTemplateType,
        last24Hours: last24HoursCount || 0,
        last7Days: last7DaysCount || 0,
      };
      
      return { data: stats, error: null };
    } catch (error) {
      return { data: null, error: error instanceof Error ? error.message : 'Failed to fetch email activity stats' };
    }
  }
}; 